import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapPrRecord } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/utils';
import { Layers, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

export const SapPrPage: React.FC = () => {
  const [prs, setPrs] = useState<SapPrRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form state for ME51N simulation
  const [requestNumber, setRequestNumber] = useState('REQ-1008');
  const [plantCode, setPlantCode] = useState('PLANT-101');
  const [costCenterCode, setCostCenterCode] = useState('CC-ENG-402');
  const [materialCode, setMaterialCode] = useState('MAT-9901');
  const [quantity, setQuantity] = useState(5);
  const [unitPrice] = useState(24500);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState<{ success: boolean; text: string } | null>(null);

  const loadPrs = async () => {
    try {
      setIsLoading(true);
      const data = await mockSapSimulatorService.getPrs();
      setPrs(data.data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPrs();
  }, []);

  const handleSimulatePrCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setResultMsg(null);
      const res = await mockSapSimulatorService.validateAndCreatePr({
        requestNumber,
        companyCode: '1000',
        plantCode,
        costCenterCode,
        glAccountCode: 'GL-400100',
        materialCode,
        materialDescription: 'ThinkPad P1 Workstation',
        quantity: Number(quantity),
        uom: 'EA',
        unitPrice: Number(unitPrice),
        currency: 'INR',
        deliveryDate: '2026-09-30',
      });

      if (res.success && res.pr) {
        setResultMsg({
          success: true,
          text: `SAP PR ${res.pr.sapPrNumber} created successfully! (Corr ID: ${res.correlationId})`,
        });
        await loadPrs();
      } else {
        setResultMsg({
          success: false,
          text: res.error || 'SAP Validation Failed',
        });
      }
    } catch (err: any) {
      setResultMsg({ success: false, text: err.message || 'PR creation failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<SapPrRecord>[] = [
    {
      header: 'SAP PR Number',
      accessorKey: 'sapPrNumber',
      cell: (row) => <span className="font-mono font-extrabold text-primary text-xs">{row.sapPrNumber}</span>,
    },
    {
      header: 'ProcureAI Request #',
      accessorKey: 'requestNumber',
      cell: (row) => <span className="font-bold text-xs text-foreground">{row.requestNumber}</span>,
    },
    {
      header: 'Material',
      cell: (row) => (
        <div className="text-xs">
          <span className="font-mono font-bold text-foreground">{row.materialCode}</span>
          <p className="text-[11px] text-foreground-secondary truncate max-w-xs">{row.materialDescription}</p>
        </div>
      ),
    },
    {
      header: 'Qty & Price',
      cell: (row) => (
        <span className="text-xs font-semibold text-foreground">
          {row.quantity} {row.uom} @ {formatCurrency(row.unitPrice, row.currency)}
        </span>
      ),
    },
    {
      header: 'Total Value',
      accessorKey: 'totalPrice',
      cell: (row) => <span className="font-extrabold text-xs text-primary">{formatCurrency(row.totalPrice, row.currency)}</span>,
    },
    {
      header: 'Plant / Cost Center',
      cell: (row) => (
        <span className="font-mono text-xs text-foreground-secondary">
          {row.plantCode} / {row.costCenterCode}
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" /> SAP Purchase Requisitions (ME51N)
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Simulated BAPI_REQUISITION_CREATE engine with validation rules, budget checking, and idempotency protection.
          </p>
        </div>

        {/* BAPI Simulation Trigger Form */}
        <Card className="border-border rounded-2xl shadow-2xs">
          <CardHeader className="pb-3 border-b border-border/60">
            <CardTitle className="font-heading text-xs font-bold text-foreground uppercase flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-primary" /> Test ME51N PR Creation BAPI Interface
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {resultMsg && (
              <div
                className={`p-3 rounded-xl border text-xs font-bold flex items-center gap-2 ${
                  resultMsg.success
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                }`}
              >
                {resultMsg.success ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
                <span>{resultMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleSimulatePrCreate} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
              <div>
                <label className="font-bold text-foreground block mb-1">Request #</label>
                <input
                  type="text"
                  value={requestNumber}
                  onChange={(e) => setRequestNumber(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-foreground block mb-1">Plant Code</label>
                <select
                  value={plantCode}
                  onChange={(e) => setPlantCode(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-semibold text-foreground"
                >
                  <option value="PLANT-101">PLANT-101</option>
                  <option value="PLANT-102">PLANT-102</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-foreground block mb-1">Cost Center</label>
                <select
                  value={costCenterCode}
                  onChange={(e) => setCostCenterCode(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-semibold text-foreground"
                >
                  <option value="CC-ENG-402">CC-ENG-402 (Engineering)</option>
                  <option value="CC-FAC-201">CC-FAC-201 (Facilities)</option>
                  <option value="CC-INVALID-99">CC-INVALID-99 (Trigger Err)</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-foreground block mb-1">Material</label>
                <select
                  value={materialCode}
                  onChange={(e) => setMaterialCode(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-semibold text-foreground"
                >
                  <option value="MAT-9901">MAT-9901 (ThinkPad P1)</option>
                  <option value="MAT-8802">MAT-8802 (Chiller Unit)</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-foreground block mb-1">Qty</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
                />
              </div>

              <div className="flex items-end">
                <Button type="submit" disabled={isSubmitting} className="w-full rounded-xl font-bold bg-primary text-white">
                  {isSubmitting ? 'Validating...' : 'Call BAPI_PR_CREATE'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* PR Records Table */}
        <DataTable data={prs} columns={columns} isLoading={isLoading} />
      </div>
    </SAPLayout>
  );
};
